/**
 * Mounts one embedded Superset dashboard via the internal embed host
 * (src/lib/supersetEmbed.ts — see its module comment for why it replaced
 * @superset-ui/embedded-sdk).
 *
 * `fetchGuestToken` is re-invoked before every guest-token expiry (~5 min)
 * and MUST stay a plain re-callable closure that mints fresh every time
 * (never memoize the token), so a revoked grant takes effect on the very
 * next mint rather than being masked by a cached credential. A failed
 * refresh mint surfaces through onError → the error banner; Retry remounts.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { embedSupersetDashboard, type SupersetEmbedHandle } from '@/lib/supersetEmbed';
import { analyticsApi } from '@/api/endpoints/analytics';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { createLogger } from '@/utils/logger';

const logger = createLogger('SupersetDashboard');

// Progressive-feedback threshold (frontend/CLAUDE.md's table): past this, the
// bare skeleton gains a "taking longer than usual" hint and a user-initiated
// Retry — a UI communication cadence like the trial-countdown tick, NOT a
// timeout (nothing aborts; Superset cold starts legitimately take this long
// and the embed keeps loading underneath). Deliberately ONE band, not the
// full five-band table: that table is written for the bedside nurse's
// data-fetching states, while here the mounted iframe carries its OWN
// in-content progressive states (Superset's per-chart "Waiting on
// database…") — this hint only covers the pre-iframe window on a desk-side
// admin surface, where a single honest "slow + retry" band is the whole
// story.
const SLOW_LOAD_HINT_MS = 15_000;

export interface SupersetDashboardProps {
  slug: string;
  embeddedUuid: string;
  embedHost: string;
}

export function SupersetDashboard({ slug, embeddedUuid, embedHost }: SupersetDashboardProps) {
  const { t } = useTranslation();
  const mountRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [slowLoad, setSlowLoad] = useState(false);

  // Elapsed-time hint while loading (reset per attempt). The user initiates
  // any abort via Retry — never a timer.
  useEffect(() => {
    if (status !== 'loading') {
      setSlowLoad(false);
      return;
    }
    setSlowLoad(false);
    const id = setTimeout(() => setSlowLoad(true), SLOW_LOAD_HINT_MS);
    return () => clearTimeout(id);
  }, [status, attempt]);

  useEffect(() => {
    const mountPoint = mountRef.current;
    if (!mountPoint) return;
    setStatus('loading');
    let cancelled = false;
    let embedded: SupersetEmbedHandle | undefined;

    embedSupersetDashboard({
      uuid: embeddedUuid,
      supersetDomain: embedHost,
      mountPoint,
      fetchGuestToken: () => analyticsApi.getGuestToken(slug).then((res) => res.token),
      onError: (error: unknown) => {
        if (cancelled) return;
        logger.error('Guest token refresh failed', { slug, error });
        setStatus('error');
      },
      hideTitle: true,
    })
      .then((handle) => {
        if (cancelled) {
          handle.unmount();
          return;
        }
        embedded = handle;
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error('Failed to embed dashboard', { slug, error });
        setStatus('error');
      });

    return () => {
      cancelled = true;
      embedded?.unmount();
    };
  }, [slug, embeddedUuid, embedHost, attempt]);

  return (
    <div className="space-y-2">
      {status === 'error' && (
        <div className="p-4 bg-red-50 rounded-xl text-sm">
          <div className="flex items-start justify-between gap-4">
            <p className="text-red-600">
              {t('admin.analytics.dash.loadError', 'Failed to load dashboard — please retry.')}
            </p>
            <Button
              onClick={() => setAttempt((a) => a + 1)}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 shrink-0"
            >
              <RefreshCw className="w-4 h-4" />
              {t('actions.retry')}
            </Button>
          </div>
        </div>
      )}
      {status === 'loading' && slowLoad && (
        <div className="p-4 bg-neutral-50 rounded-xl text-sm">
          <div className="flex items-start justify-between gap-4">
            <p className="text-neutral-600">
              {t(
                'admin.analytics.dash.slowLoad',
                'Taking longer than usual — the analytics backend may be starting up.',
              )}
            </p>
            <Button
              onClick={() => setAttempt((a) => a + 1)}
              variant="outline"
              size="sm"
              className="flex items-center gap-2 shrink-0"
            >
              <RefreshCw className="w-4 h-4" />
              {t('actions.retry')}
            </Button>
          </div>
        </div>
      )}
      <div className="relative min-h-[600px] rounded-xl overflow-hidden bg-white border border-neutral-200">
        {status === 'loading' && <Skeleton className="absolute inset-0" />}
        <div
          ref={mountRef}
          data-testid={`superset-dashboard-${slug}`}
          className="w-full h-full min-h-[600px] [&>iframe]:w-full [&>iframe]:h-full [&>iframe]:min-h-[600px] [&>iframe]:border-0"
        />
      </div>
    </div>
  );
}
